import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { CompletionType, ReturnEvidenceRequest } from '../../../types/custody-actions'
import { usePerformCustodyAction } from '../../../hooks/use-custody-actions'
import { EvidenceItemSelector } from './EvidenceItemSelector'

interface ReturnEvidenceFormProps {
  caseId: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

interface PartyFieldsProps {
  prefix: string
  nameValue: string
  roleValue: string
  orgValue: string
  onNameChange: (val: string) => void
  onRoleChange: (val: string) => void
  onOrgChange: (val: string) => void
}

function PartyFields({
  prefix,
  nameValue,
  roleValue,
  orgValue,
  onNameChange,
  onRoleChange,
  onOrgChange,
}: PartyFieldsProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <fieldset className="space-y-2 rounded-lg border border-[var(--border)] p-3">
      <legend className="px-1 text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
        {prefix}
      </legend>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label
            htmlFor={`${prefix}-rname`}
            className="mb-1 block text-xs text-[var(--text-muted)]"
          >
            {t('custodyActions.name')}
          </label>
          <input
            id={`${prefix}-rname`}
            type="text"
            value={nameValue}
            onChange={(ev) => {
              onNameChange(ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label
            htmlFor={`${prefix}-rrole`}
            className="mb-1 block text-xs text-[var(--text-muted)]"
          >
            {t('custodyActions.role')}
          </label>
          <input
            id={`${prefix}-rrole`}
            type="text"
            value={roleValue}
            onChange={(ev) => {
              onRoleChange(ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label htmlFor={`${prefix}-rorg`} className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.organisation')}
          </label>
          <input
            id={`${prefix}-rorg`}
            type="text"
            value={orgValue}
            onChange={(ev) => {
              onOrgChange(ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
      </div>
    </fieldset>
  )
}

interface ReturnFormState {
  selectedIds: string[]
  completionType: CompletionType
  actionDate: string
  actionTime: string
  fromName: string
  fromRole: string
  fromOrg: string
  toName: string
  toRole: string
  toOrg: string
  method: string
  notes: string
}

const DATE_SLICE_END = 10
const TIME_SLICE_END = 5

function buildInitialState(): ReturnFormState {
  const now = new Date()
  return {
    selectedIds: [],
    completionType: 'complete',
    actionDate: now.toISOString().slice(0, DATE_SLICE_END),
    actionTime: now.toTimeString().slice(0, TIME_SLICE_END),
    fromName: '',
    fromRole: '',
    fromOrg: '',
    toName: '',
    toRole: '',
    toOrg: '',
    method: '',
    notes: '',
  }
}

function buildRequest(state: ReturnFormState): ReturnEvidenceRequest {
  const req: ReturnEvidenceRequest = {
    evidenceNumberIds: state.selectedIds,
    completionType: state.completionType,
    actionDate: state.actionDate,
    actionTime: state.actionTime,
    fromName: state.fromName,
    toName: state.toName,
  }
  if (state.fromRole.length > 0) req.fromRole = state.fromRole
  if (state.fromOrg.length > 0) req.fromOrganisation = state.fromOrg
  if (state.toRole.length > 0) req.toRole = state.toRole
  if (state.toOrg.length > 0) req.toOrganisation = state.toOrg
  if (state.method.length > 0) req.method = state.method
  if (state.notes.length > 0) req.notes = state.notes
  return req
}

type Updater = <K extends keyof ReturnFormState>(key: K, value: ReturnFormState[K]) => void

interface ReturnSelectorSectionProps {
  caseId: string
  state: ReturnFormState
  update: Updater
}

function ReturnSelectorSection({
  caseId,
  state,
  update,
}: ReturnSelectorSectionProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <>
      <EvidenceItemSelector
        caseId={caseId}
        selectedIds={state.selectedIds}
        onChange={(ids) => {
          update('selectedIds', ids)
        }}
      />
      <div className="flex gap-4">
        <div className="flex-1">
          <label htmlFor="ret-date" className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.actionDate')}
          </label>
          <input
            id="ret-date"
            type="date"
            value={state.actionDate}
            onChange={(ev) => {
              update('actionDate', ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="ret-time" className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.actionTime')}
          </label>
          <input
            id="ret-time"
            type="time"
            value={state.actionTime}
            onChange={(ev) => {
              update('actionTime', ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
      </div>
    </>
  )
}

interface ReturnCompletionSectionProps {
  state: ReturnFormState
  update: Updater
}

function ReturnCompletionSection({
  state,
  update,
}: ReturnCompletionSectionProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div>
      <p className="mb-1 text-xs text-[var(--text-muted)]">
        {t('custodyActions.completionType')}
      </p>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="ret-completion"
            value="complete"
            checked={state.completionType === 'complete'}
            onChange={() => {
              update('completionType', 'complete')
            }}
          />
          {t('custodyActions.complete')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="ret-completion"
            value="partial"
            checked={state.completionType === 'partial'}
            onChange={() => {
              update('completionType', 'partial')
            }}
          />
          {t('custodyActions.partial')}
        </label>
      </div>
    </div>
  )
}

interface ReturnNotesSectionProps {
  state: ReturnFormState
  update: Updater
  error: string | null
}

function ReturnNotesSection({
  state,
  update,
  error,
}: ReturnNotesSectionProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <>
      <div>
        <label htmlFor="ret-method" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.method')}
        </label>
        <input
          id="ret-method"
          type="text"
          value={state.method}
          onChange={(ev) => {
            update('method', ev.target.value)
          }}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
      <div>
        <label htmlFor="ret-notes" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.notes')}
        </label>
        <textarea
          id="ret-notes"
          value={state.notes}
          onChange={(ev) => {
            update('notes', ev.target.value)
          }}
          rows={2}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
      {error && (
        <div role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}
    </>
  )
}

interface ReturnFormFieldsProps {
  caseId: string
  state: ReturnFormState
  update: Updater
  error: string | null
}

function ReturnFormFields({
  caseId,
  state,
  update,
  error,
}: ReturnFormFieldsProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div className="space-y-4 overflow-y-auto p-6">
      <ReturnSelectorSection caseId={caseId} state={state} update={update} />
      <ReturnCompletionSection state={state} update={update} />
      <PartyFields
        prefix={t('custodyActions.from')}
        nameValue={state.fromName}
        roleValue={state.fromRole}
        orgValue={state.fromOrg}
        onNameChange={(val) => { update('fromName', val) }}
        onRoleChange={(val) => { update('fromRole', val) }}
        onOrgChange={(val) => { update('fromOrg', val) }}
      />
      <PartyFields
        prefix={t('custodyActions.to')}
        nameValue={state.toName}
        roleValue={state.toRole}
        orgValue={state.toOrg}
        onNameChange={(val) => { update('toName', val) }}
        onRoleChange={(val) => { update('toRole', val) }}
        onOrgChange={(val) => { update('toOrg', val) }}
      />
      <ReturnNotesSection state={state} update={update} error={error} />
    </div>
  )
}

export function ReturnEvidenceForm({
  caseId,
  open,
  onClose,
  onSuccess,
}: ReturnEvidenceFormProps): React.ReactElement | null {
  const { t } = useTranslation('evidence')
  const [state, setState] = useState<ReturnFormState>(buildInitialState)
  const { performing, error, performReturn, clearError } = usePerformCustodyAction()

  const update = useCallback(
    <K extends keyof ReturnFormState>(key: K, value: ReturnFormState[K]) => {
      setState((prev) => ({ ...prev, [key]: value }))
    },
    []
  )

  const handleSubmit = useCallback(async () => {
    const result = await performReturn(caseId, buildRequest(state))
    if (result !== null) onSuccess()
  }, [state, caseId, performReturn, onSuccess])

  const handleClose = useCallback(() => {
    clearError()
    onClose()
  }, [clearError, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg border border-[var(--border)] bg-[var(--bg-card)] shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
          <h2 className="text-lg font-[var(--font-heading)] font-semibold text-[var(--text-primary)]">
            {t('custodyActions.return')}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="rounded p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <ReturnFormFields caseId={caseId} state={state} update={update} error={error} />
        <div className="flex justify-end gap-2 border-t border-[var(--border)] px-6 py-4">
          <button
            type="button"
            onClick={handleClose}
            className="rounded border border-[var(--border)] px-4 py-2 text-xs font-[var(--font-mono)] tracking-wider text-[var(--text-primary)] uppercase hover:opacity-80"
          >
            {t('custodyActions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              void handleSubmit()
            }}
            disabled={performing || state.selectedIds.length === 0}
            className="rounded bg-[var(--accent)] px-4 py-2 text-xs font-[var(--font-mono)] tracking-wider text-white uppercase hover:opacity-90 disabled:opacity-50"
          >
            {t('custodyActions.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}
